import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { apiClient } from '@workspace/public-utils'
import { Seo } from '../components/Seo'

interface ConsentPageData {
  tier_name: string
  tier_description?: string
  terms_content: string
  terms_content_type: 'txt' | 'md'
  customer_name: string
  status: 'pending' | 'consented' | 'expired'
  expires_at: string
  already_consented: boolean
  consented_at?: string
}

const TermsConsentPage = () => {
  const { consentPath } = useParams<{ consentPath: string }>()
  const [data, setData] = useState<ConsentPageData | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string>('')
  const [agreed, setAgreed] = useState<boolean>(false)
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [submitted, setSubmitted] = useState<boolean>(false)

  useEffect(() => {
    const loadConsent = async () => {
      if (!consentPath) {
        setError('유효하지 않은 링크입니다.')
        setLoading(false)
        return
      }

      try {
        const res = await apiClient.get(`/api/v1/public/plans/consent/${consentPath}`)
        setData(res.data)
      } catch (err: any) {
        const detail = err?.response?.data?.detail || '페이지를 불러올 수 없습니다.'
        setError(detail)
      } finally {
        setLoading(false)
      }
    }
    loadConsent()
  }, [consentPath])

  const handleSubmit = async () => {
    if (!agreed || !consentPath) return

    const token = consentPath

    try {
      setSubmitting(true)
      const res = await apiClient.post('/api/v1/public/plans/consent', {
        consent_token: token,
        agreed: true,
      })
      if (res.data?.success) {
        setSubmitted(true)
        // Re-fetch to get updated data with consented_at and full terms
        try {
          const updated = await apiClient.get(`/api/v1/public/plans/consent/${consentPath}`)
          setData(updated.data)
        } catch {
          // If re-fetch fails, manually update the local state
          if (data) {
            setData({
              ...data,
              status: 'consented',
              already_consented: true,
              consented_at: res.data.consented_at,
            })
          }
        }
      }
    } catch (err: any) {
      const detail = err?.response?.data?.detail || '동의 처리에 실패했습니다.'
      setError(detail)
    } finally {
      setSubmitting(false)
    }
  }

  // Render states
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-400 mx-auto mb-4"></div>
          <p className="text-slate-400">약관을 불러오는 중...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Seo title="약관 동의 오류" description="약관 동의 페이지" />
        <div className="max-w-md w-full bg-slate-800/50 backdrop-blur border border-red-500/30 rounded-2xl p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-500/10 flex items-center justify-center">
            <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-white mb-3">오류가 발생했습니다</h1>
          <p className="text-slate-400">{error}</p>
        </div>
      </div>
    )
  }

  // Expired state
  if (data?.status === 'expired') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Seo title="만료된 링크" description="약관 동의 링크가 만료되었습니다" />
        <div className="max-w-md w-full bg-slate-800/50 backdrop-blur border border-yellow-500/30 rounded-2xl p-8 text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-yellow-500/10 flex items-center justify-center">
            <svg className="w-8 h-8 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-white mb-3">링크가 만료되었습니다</h1>
          <p className="text-slate-400">
            이 약관 동의 링크는 만료되었습니다.<br />
            새로운 링크를 요청해 주세요.
          </p>
        </div>
      </div>
    )
  }

  // Already consented or just submitted — read-only terms view
  if (data?.already_consented || submitted) {
    const consentedDate = data?.consented_at
      ? new Date(data.consented_at).toLocaleString('ko-KR', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : null

    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-8 px-4">
        <Seo title="동의 완료" description="약관 동의가 완료되었습니다" />

        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/30 rounded-full mb-4">
              <span className="text-amber-400 font-semibold">{data?.tier_name}</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
              서비스 이용약관
            </h2>
            {data?.customer_name && (
              <p className="text-slate-400">
                <span className="text-white font-medium">{data.customer_name}</span>님이 동의하신 약관 내용입니다.
              </p>
            )}
          </div>

          {/* Read-only terms content */}
          <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-2xl overflow-hidden mb-6">
            <div className="bg-slate-700/30 px-6 py-4 border-b border-slate-700/50">
              <h3 className="font-semibold text-white">이용약관</h3>
              {data?.tier_description && (
                <p className="text-sm text-slate-400 mt-1">{data.tier_description}</p>
              )}
            </div>
            <div className="p-6 max-h-[50vh] overflow-y-auto">
              <pre className="whitespace-pre-wrap text-sm text-slate-300 font-sans leading-relaxed">
                {data?.terms_content}
              </pre>
            </div>
          </div>

          {/* Consent confirmation banner */}
          <div className="bg-green-500/10 border border-green-500/30 rounded-2xl p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex-shrink-0 rounded-full bg-green-500/20 flex items-center justify-center">
                <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h1 className="text-lg font-bold text-white">약관 동의가 완료되었습니다</h1>
                <p className="text-sm text-slate-400 mt-0.5">
                  <span className="text-white font-medium">{data?.customer_name}</span>님이{' '}
                  <span className="text-amber-400 font-medium">{data?.tier_name}</span> 플랜 약관에 동의하셨습니다.
                  {consentedDate && (
                    <span className="text-slate-500"> ({consentedDate})</span>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Main consent form
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-8 px-4">
      <Seo 
        title={`${data?.tier_name || ''} 약관 동의`} 
        description="구독 플랜 약관 동의 페이지"
      />
      
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/30 rounded-full mb-4">
            <span className="text-amber-400 font-semibold">{data?.tier_name}</span>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
            서비스 이용약관 동의
          </h1>
          {data?.customer_name && (
            <p className="text-slate-400">
              <span className="text-white font-medium">{data.customer_name}</span>님, 아래 약관을 확인해 주세요.
            </p>
          )}
        </div>

        {/* Terms content */}
        <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-2xl overflow-hidden mb-6">
          <div className="bg-slate-700/30 px-6 py-4 border-b border-slate-700/50">
            <h2 className="font-semibold text-white">이용약관</h2>
            {data?.tier_description && (
              <p className="text-sm text-slate-400 mt-1">{data.tier_description}</p>
            )}
          </div>
          <div className="p-6 max-h-[50vh] overflow-y-auto">
            <pre className="whitespace-pre-wrap text-sm text-slate-300 font-sans leading-relaxed">
              {data?.terms_content}
            </pre>
          </div>
        </div>

        {/* Agreement checkbox and button */}
        <div className="bg-slate-800/50 backdrop-blur border border-slate-700/50 rounded-2xl p-6">
          <label className="flex items-start gap-4 cursor-pointer group">
            <div className="relative flex-shrink-0 mt-0.5">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-6 h-6 border-2 border-slate-500 rounded-md peer-checked:bg-amber-500 peer-checked:border-amber-500 transition-all flex items-center justify-center">
                {agreed && (
                  <svg className="w-4 h-4 text-slate-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
            </div>
            <div className="flex-1">
              <span className="text-white font-medium group-hover:text-amber-300 transition-colors">
                위 약관 내용을 모두 확인하였으며, 이에 동의합니다.
              </span>
              <p className="text-sm text-slate-400 mt-1">
                약관에 동의하시면 해당 구독 플랜의 서비스를 이용하실 수 있습니다.
              </p>
            </div>
          </label>

          <button
            onClick={handleSubmit}
            disabled={!agreed || submitting}
            className={`
              w-full mt-6 py-4 px-6 rounded-xl font-semibold text-lg transition-all
              ${agreed 
                ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 hover:from-amber-400 hover:to-amber-500 shadow-lg shadow-amber-500/25' 
                : 'bg-slate-700 text-slate-400 cursor-not-allowed'
              }
              disabled:opacity-50 disabled:cursor-not-allowed
            `}
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                처리 중...
              </span>
            ) : (
              '동의하기'
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default TermsConsentPage
